'use client'

import ViverseSkeleton from '@/components/ViverseSkeleton'
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton'
import {
  RECOMMEND_COLUMN_WIDTH,
  RECOMMEND_GRID_DESKTOP,
  WORLD_CONTENT_COL_WIDTH,
  WORLD_DETAIL_SECTION_GRID,
} from '@/constants/content-detail-layout'
import { useLayoutContext } from '@/context/LayoutContext'
import { useUAContext } from '@/context/UAContext'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useEnableTransparentBackground } from '@/hooks/useEnableTransparentBackground'
import { cn } from '@/lib/utils'

export default function WorldPageSkeleton() {
  const { headerHeight } = useLayoutContext()
  const { isMobileApp } = useUAContext()
  const enableFull3d = useEnableFull3d()
  const enableTransparentBackground = useEnableTransparentBackground()

  // The detail-page skeleton would paint over the host page of a transparent embed.
  if (enableTransparentBackground) {
    return <main data-transparent-background className="flex-1" />
  }

  return (
    <main
      className={cn('relative flex flex-1 flex-col', {
        'min-[1280px]:pl-15': !isMobileApp && !enableFull3d,
      })}
      style={{ paddingTop: headerHeight }}
    >
      <section className={cn('grid grid-cols-1 gap-12 md:px-8 md:py-6', WORLD_DETAIL_SECTION_GRID)}>
        {/* Left: player + info panel */}
        <div
          className={cn(
            WORLD_CONTENT_COL_WIDTH,
            'flex w-full flex-col gap-4 md:max-w-full md:min-w-full md:gap-6 lg:col-start-1 lg:row-start-1',
          )}
        >
          <ViverseSkeleton className="aspect-video w-full md:rounded-xl" />

          {/* Info panel */}
          <div className="flex flex-col gap-4 px-4 lg:px-0">
            <div className="flex flex-col gap-2">
              <ViverseSkeleton className="h-7 w-3/4 rounded-lg" />
              <ViverseSkeleton className="h-5 w-1/2 rounded-lg" />
            </div>

            {/* Creator row */}
            <div className="flex items-center gap-3">
              <ViverseSkeleton className="size-10 shrink-0 rounded-full" />
              <ViverseSkeleton className="h-4 w-32 rounded-lg" />
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <ViverseSkeleton className="h-10 w-28 rounded-lg" />
              <ViverseSkeleton className="h-10 w-10 rounded-lg" />
              <ViverseSkeleton className="h-10 w-10 rounded-lg" />
            </div>
          </div>
        </div>

        {/* Right: recommend list (lg: col 2, spans 2 rows) */}
        <div
          className={cn(
            'no-scrollbar flex flex-col max-lg:overflow-scroll lg:col-start-2 lg:row-span-2',
            RECOMMEND_COLUMN_WIDTH,
          )}
        >
          <div className={cn('flex gap-2', RECOMMEND_GRID_DESKTOP)}>
            {Array.from({ length: 12 }).map((_, index) => (
              <ViverseWorldCardSkeleton
                key={index}
                className="aspect-[1.72] w-full"
              />
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
